import { useEffect, useRef } from "react";
import {
  signInWithCredential,
  GoogleAuthProvider,
  getAuth,
} from "firebase/auth";

// Extend the Window interface to include google and GOOGLE_CLIENT_ID for TypeScript
declare global {
  interface Window {
    google: any; // The Google Identity Services object
    GOOGLE_CLIENT_ID: string; // The Google Client ID
  }
}

interface GoogleSignInButtonProps {
  onSignInSuccess?: () => void;
  onSignInError?: (error: any) => void;
}

export function GoogleSignInButton({
  onSignInSuccess,
  onSignInError,
}: GoogleSignInButtonProps) {
  const buttonRef = useRef<HTMLDivElement>(null); // Ref for the div where Google button will render

  // Using AuthContext for user state management (optional but common)
  // const { user, loading } = useAuth(); // Example context usage

  useEffect(() => {
    if (!window.google || !buttonRef.current) {
      console.warn(
        "Google Identity Services script not loaded or button ref not available."
      );
      return;
    }
    console.log("Google Identity Services script loaded successfully.");

    // This is the callback function that Google will call after a successful sign-in
    // It receives the credential (ID token) from Google
    const handleCredentialResponse = async (response: any) => {
      // response is of type google.accounts.id.CredentialResponse
      if (response.credential) {
        const idToken = response.credential;

        // Authenticate with Firebase using the Google ID Token
        try {
          const credential = GoogleAuthProvider.credential(idToken);
          const auth = getAuth(); // Get the Firebase Auth instance
          await signInWithCredential(auth, credential);
          onSignInSuccess?.();
        } catch (firebaseError) {
          onSignInError?.(firebaseError);
        }
      } else {
        onSignInError?.(
          new Error("Google Sign-In failed: No credential received.")
        );
      }
    };

    // Initialize Google's identity service
    window.google.accounts.id.initialize({
      client_id: window.googleClientId,
      callback: handleCredentialResponse,
      auto_select: true, // Automatically select a user if only one is logged in to Google
      ux_mode: "popup", // 'popup' or 'redirect'. 'popup' is often preferred for SPAs.
    });

    // Render the Google Sign-In button into the div referenced by buttonRef
    window.google.accounts.id.renderButton(buttonRef.current, {
      theme: "filled_blue", // 'outline' or 'filled_blue'
      size: "large", // 'large', 'medium', 'small'
      text: "signin_with", // 'signin_with', 'signup_with', 'continue_with', 'federated_signin_title'
      shape: "rectangular", // 'rectangular', 'pill', 'circle', 'square'
      logo_alignment: "left", // 'left', 'center'
    });
  }, []);

  return (
    // The div where the Google button will be rendered by Google's script
    <div
      ref={buttonRef}
      style={{
        display: "flex", // Ensures button is centered if width is less than container
        justifyContent: "center", // Centers the button horizontally
        alignItems: "center", // Centers the button vertically
        // Essential for removing white trim:
        backgroundColor: "transparent", // Or match button background if needed
        border: "none",
        outline: "none",
        boxShadow: "none",
        borderRadius: "4px",
        overflow: "hidden", // Helps hide any overflow from within the iframe if it's slightly off
      }}
    ></div>
  );
}
